<script setup>
import {computed, reactive, ref} from "vue";
import axios from "axios";

import MobileTopBar from "@js/components/TopBar/MobileTopBar.vue";
import Breadcrumbs from "@js/components/Breadcrumbs/Breadcrumbs.vue";
import SpinnerIndicator from "@js/components/Statuses/SpinnerIndicator.vue";
import IconExcelColored from "@js/components/Icons/IconExcelColored.vue";
import IconChevronDown from "@js/components/Icons/IconChevronDown.vue";

const props = defineProps({
    priceListData: {
        type: Array,
        required: true,
    },
});

const today = new Date().toLocaleDateString("ru-RU", {
    timeZone: "Europe/Moscow",
});

const selected = reactive({
    fullCats: new Set(), // категории, выбранные родительским тумблером целиком
    subs: new Map(),     // catId -> Set(subId)
    opened: new Set(),
});

const downloading = ref(false);

const getSubIds = (cat) => {
    return Array.isArray(cat.sub) ? cat.sub.map(item => item.id) : [];
};

const ensureSubSet = (catId) => {
    if (!selected.subs.has(catId)) {
        selected.subs.set(catId, new Set());
    }

    return selected.subs.get(catId);
};

const selectedSubCount = (catId) => {
    return selected.subs.get(catId)?.size ?? 0;
};

const isCatSelected = (catId) => {
    return selected.fullCats.has(catId) || selectedSubCount(catId) > 0;
};

const isSubSelected = (catId, subId) => {
    return selected.subs.get(catId)?.has(subId) ?? false;
};

const isOpened = (catId) => selected.opened.has(catId);

const toggleOpened = (catId) => {
    if (selected.opened.has(catId)) {
        selected.opened.delete(catId);
    } else {
        selected.opened.add(catId);
    }
};

const toggleCat = (cat) => {
    const catId = cat.id;
    const allSubIds = getSubIds(cat);
    const currentlySelected = isCatSelected(catId);

    if (currentlySelected) {
        selected.fullCats.delete(catId);
        selected.subs.delete(catId);
        return;
    }

    selected.fullCats.add(catId);
    selected.opened.add(catId);
    selected.subs.set(catId, new Set(allSubIds));
};

const toggleSub = (cat, subId) => {
    const catId = cat.id;
    const set = ensureSubSet(catId);

    if (set.has(subId)) {
        set.delete(subId);
    } else {
        set.add(subId);
        selected.opened.add(catId);
    }

    // как только вручную изменили детей, категория больше не считается "полностью выбранной"
    selected.fullCats.delete(catId);

    if (set.size === 0) {
        selected.subs.delete(catId);
    }
};

const hasSelection = computed(() => {
    if (selected.fullCats.size > 0) {
        return true;
    }

    for (const set of selected.subs.values()) {
        if (set.size > 0) {
            return true;
        }
    }

    return false;
});

const selectedSummary = computed(() => {
    let categoriesCount = 0;
    let subCategoriesCount = 0;

    for (const cat of props.priceListData) {
        const catId = cat.id;

        if (selected.fullCats.has(catId)) {
            categoriesCount++;
            continue;
        }

        subCategoriesCount += selected.subs.get(catId)?.size ?? 0;
    }

    return {
        categoriesCount,
        subCategoriesCount,
    };
});

const buildPayload = () => {
    const categories = Array.from(selected.fullCats);
    const sub_categories = [];

    selected.subs.forEach((set, catId) => {
        if (selected.fullCats.has(catId)) {
            return;
        }

        set.forEach((subId) => sub_categories.push(subId));
    });

    return {
        categories,
        sub_categories,
    };
};

const clearSelection = () => {
    selected.fullCats.clear();
    selected.subs.clear();
};

const downloadPriceList = async () => {
    if (downloading.value) return;

    downloading.value = true;

    try {
        const response = await axios.post(
            "/axios/price-list",
            buildPayload(),
            {
                responseType: "blob",
            }
        );

        let filename = "pricelist.xlsx";
        const contentDisposition = response.headers["content-disposition"];

        if (contentDisposition) {
            const match = /filename\*?=(?:UTF-8''|")?([^";]+)"?/i.exec(contentDisposition);

            if (match?.[1]) {
                filename = decodeURIComponent(match[1]);
            }
        }

        const blob = new Blob(
            [response.data],
            {
                type: response.headers["content-type"]
                    || "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            }
        );

        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.href = url;
        link.download = filename;

        document.body.appendChild(link);
        link.click();
        link.remove();

        URL.revokeObjectURL(url);
    } catch (error) {
        console.error(error);
    } finally {
        downloading.value = false;
    }
};
</script>

<template>
    <MobileTopBar/>

    <div class="container">
        <Breadcrumbs/>

        <h1 class="mb-3 text-2xl font-bold">
            Прайс-лист
        </h1>

        <div class="mb-5 flex gap-5 max-md:flex-col md:items-center">
            <button
                @click.prevent="downloadPriceList"
                :disabled="downloading"
                class="order-2 flex items-center gap-4 rounded-lg bg-[#3ca241] px-5 py-3 text-white disabled:cursor-not-allowed disabled:opacity-50 max-sm:w-full max-sm:justify-center md:order-1 md:hover:bg-[#3ca241]/80"
            >
                <IconExcelColored
                    v-if="!downloading"
                    class="h-[30px] w-auto text-white"
                    first_color="#fff"
                    second_color="#3ca241"
                />

                <SpinnerIndicator v-else class="h-[30px] w-auto"/>

                <span class="flex flex-col items-start">
                    <span class="font-medium" v-if="!downloading">
                        {{ hasSelection ? 'Скачать выбранный прайс-лист' : 'Скачать полный прайс-лист' }}
                    </span>
                    <span v-else>
                        Идет скачивание...
                    </span>
                    <span class="text-xs font-medium">
                        Формат Excel (.xlsx)
                    </span>
                </span>
            </button>

            <div class="order-1 flex flex-col gap-1 text-xs font-semibold md:order-2">
                <div class="flex items-center gap-2">
                    <div class="relative flex size-3">
                        <div class="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-600 opacity-75"></div>
                        <div class="relative inline-flex size-3 rounded-full bg-emerald-600"></div>
                    </div>
                    Цены актуальны на {{ today }}
                </div>

                <div>
                    Цены меняются автоматически, при изменениях цен на сайте,
                    <br class="max-md:hidden"/>
                    прайс-лист всегда актуальный.
                </div>
                <div v-if="hasSelection" class="pt-1 text-[13px] text-gray-600">
                    Выбрано:
                    <span v-if="selectedSummary.categoriesCount">
                        категорий — {{ selectedSummary.categoriesCount }}
                    </span>
                    <span v-if="selectedSummary.categoriesCount && selectedSummary.subCategoriesCount">
                        ,
                    </span>
                    <span v-if="selectedSummary.subCategoriesCount">
                        подкатегорий — {{ selectedSummary.subCategoriesCount }}
                    </span>
                </div>

            </div>

            <button
                v-if="hasSelection"
                @click.prevent="clearSelection"
                type="button"
                class="order-3 rounded-lg border border-gray-300 px-4 py-3 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
            >
                Сбросить выбор
            </button>
        </div>

        <div class="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
            <ul role="list" class="divide-y divide-gray-100">
                <li
                    v-for="cat in priceListData"
                    :key="cat.id"
                    class="px-5 pt-4 pb-3"
                >
                    <div class="flex items-start justify-between gap-4">
                        <div class="min-w-0 flex-1">
                            <div class="flex items-start gap-3">
                                <button
                                    type="button"
                                    @click="toggleOpened(cat.id)"
                                    class="mt-0.5 inline-flex shrink-0 items-center justify-center rounded-md border border-gray-200 p-1 text-gray-500 transition hover:bg-gray-50"
                                >
                                    <IconChevronDown
                                        class="h-4 w-4 transition"
                                        :class="isOpened(cat.id) ? 'rotate-180' : ''"
                                    />
                                </button>

                                <div class="min-w-0">
                                    <div class="text-lg font-medium leading-tight">
                                        {{ cat.title }}
                                    </div>
                                    <div class="text-sm font-medium text-gray-500">
                                        Товаров: {{ cat.count }}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <label class="inline-flex cursor-pointer items-center">
                            <input
                                type="checkbox"
                                class="peer sr-only"
                                :checked="isCatSelected(cat.id)"
                                @change="toggleCat(cat)"
                            >
                            <div
                                class="relative h-6 w-11 rounded-full bg-gray-200 after:absolute after:start-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-[#3ca241] peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full"
                            ></div>
                        </label>
                    </div>

                    <Transition name="collapse">
                        <ul
                            v-if="cat.sub?.length && isOpened(cat.id)"
                            class="mt-3 divide-y divide-gray-100 sm:mt-4"
                        >
                            <li
                                v-for="sub in cat.sub"
                                :key="sub.id"
                                class="py-3 pl-4 sm:pl-10"
                            >
                                <div class="flex items-start justify-between gap-4">
                                    <div>
                                        <div class="font-medium leading-tight">
                                            {{ sub.title }}
                                        </div>
                                        <div class="text-sm font-medium text-gray-500">
                                            Товаров: {{ sub.count }}
                                        </div>
                                    </div>

                                    <label class="inline-flex cursor-pointer items-center">
                                        <input
                                            type="checkbox"
                                            class="peer sr-only"
                                            :checked="isSubSelected(cat.id, sub.id)"
                                            @change="toggleSub(cat, sub.id)"
                                        >
                                        <div
                                            class="relative h-6 w-11 rounded-full bg-gray-200 after:absolute after:start-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-[#3ca241] peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full"
                                        ></div>
                                    </label>
                                </div>
                            </li>
                        </ul>
                    </Transition>
                </li>
            </ul>
        </div>
    </div>
</template>

<style scoped>
.collapse-enter-active,
.collapse-leave-active {
    transition: max-height .25s ease, opacity .2s ease;
    overflow: hidden;
}

.collapse-enter-from,
.collapse-leave-to {
    max-height: 0;
    opacity: 0;
}

.collapse-enter-to,
.collapse-leave-from {
    max-height: 1000px;
    opacity: 1;
}
</style>
